// info / files — two sections, switched at the start of the breadcrumb (?in=infocenter):
// Infocenter (pages/Infocenter.tsx): a folder per production year, in it a folder per vessel with its files and
// its serial numbers. Order Lists (below): the online counterpart of the yard's local "PRODUCTION - Order Lists"
// folder. One season folder (e.g. General 2026-2027) holds the master order sheet and a workbook per
// vessel, stored in Cloudflare R2. A season folder also carries the per-vessel order status (printed
// date, leader check, comments). Spreadsheets open in an in-browser viewer, and a folder can be
// refreshed from a local directory in one go.
// On phones the listing is a list of cards (folders are full-width rows, files carry their actions), the
// title shortens to "Info / Files" while the breadcrumb carries the path, and the model filter is one
// sideways-scrolling strip.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCreateFolder, useDeleteFile, useFiles, useOverview, useUploadFile } from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { MODEL_ORDER, modelKey, orderKeySap, unlistedOrderKey } from '@technohull/shared';
import { useOrderFiles } from '../features/bom.js';
import { fold, hasText } from '../lib/search.js';
import { WorkbookViewerModal } from '../components/WorkbookViewerModal.js';
import { isSpreadsheet, looksLikeSap, normName, proposeSapRenames, seasonFolderLabel, type SapRenameProposal } from '../features/orderList.js';
import { ApiError } from '../lib/api.js';
import { EmptyState, ErrorState, InlineError, LoadingState, Modal, OverflowActions, PageHeader, SearchInput, useConfirm } from '../components/ui.js';
import { IconChevronRight, IconFile, IconFolder, IconRefresh, IconTrash, IconUpload } from '../components/icons.js';
import { useDateFormatter } from '../lib/format.js';
import { useIsCoarse, useIsPhone } from '../lib/media.js';
import { Infocenter, RootSwitch } from './Infocenter.js';

const ORDER_LISTS_PREFIX = 'order-lists/';

/** the upload route allows 60 puts / 60s; wait a hair over the window before resuming a big folder. */
const RATE_WAIT_MS = 61_000;
const MAX_RATE_WAITS = 6;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** filenames become R2 keys — coerce to the server's allowed set so a local file uploads cleanly. */
const safeFileName = (n: string) =>
  n.replace(/[^A-Za-z0-9 _.()\-]+/g, ' ').replace(/\.{2,}/g, '.').replace(/\s+/g, ' ').trim() || 'file';

// R2 keys are validated server-side against [A-Za-z0-9 _.()-] and reject '..'; keep names inside that.
const safeName = (s: string) =>
  s.replace(/[^A-Za-z0-9 _.()-]+/g, ' ').replace(/\.{2,}/g, '.').replace(/\s+/g, ' ').trim();

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function InfoFiles() {
  const [params, setParams] = useSearchParams();
  return params.get('in') === 'infocenter'
    ? <Infocenter onOrderLists={() => setParams({})} />
    : <OrderLists onInfocenter={() => setParams({ in: 'infocenter' })} />;
}

function OrderLists({ onInfocenter }: { onInfocenter: () => void }) {
  const { can } = useAuth();
  const df = useDateFormatter();
  const canWrite = can('production:write');
  const { activeSeason, currentSeason } = useSeason();
  const [prefix, setPrefix] = useState(ORDER_LISTS_PREFIX); // always under order-lists/
  const { data, isLoading, isFetching, error, refetch } = useFiles(prefix);
  const upload = useUploadFile();
  const del = useDeleteFile();
  const mkdir = useCreateFolder();
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);
  const [msg, setMsg] = useState('');
  const [viewFile, setViewFile] = useState<{ key: string; name: string } | null>(null);
  // the active season's vessels give us name → SAP, so a "refresh from folder" can offer to prefix
  // previously-uploaded files with their SAP (e.g. "JASNA" → "9100555 - JASNA").
  const overview = useOverview(activeSeason || undefined);
  const [renamePlan, setRenamePlan] = useState<{ files: File[]; skipped: number; proposals: SapRenameProposal[] } | null>(null);
  // true for a whole upload run — upload.isPending drops between files and during a rate-limit pause
  const [busy, setBusy] = useState(false);
  const [newFolder, setNewFolder] = useState<string | null>(null); // the "+ Folder…" name dialog
  const [picking, setPicking] = useState(false); // the "New order file" boat picker
  const [confirmNode, confirm] = useConfirm();
  const isPhone = useIsPhone();
  // touch browsers can't pick a folder (webkitdirectory), so the same multi-file upload is offered by name
  const coarse = useIsCoarse();
  const crumbs = useRef<HTMLDivElement>(null);

  // path below order-lists/: [] = the season list, [season] = that season's workbooks + order status.
  const segs = prefix.slice(ORDER_LISTS_PREFIX.length).split('/').filter(Boolean);
  const level: 'seasons' | 'files' = segs.length === 0 ? 'seasons' : 'files';
  // the current production season, named as the yard's local folder (e.g. General 2026-2027).
  const seasonLabel = seasonFolderLabel(currentSeason || activeSeason);
  // inside a season, order files are filed under a per-model subfolder; segs[1] is the active model.
  const activeModel = segs[1] ?? '';

  // Skip OS/Office clutter that has no place online: dotfiles, Excel's "~$" lock files (which would
  // otherwise sanitise onto a real workbook's name and clobber it), and the usual Windows debris.
  const isJunk = (name: string) =>
    !name || name.startsWith('.') || name.startsWith('~$') || name === 'Thumbs.db' || name === 'desktop.ini';

  // upload one or many files into the current folder, sanitising each name to a valid R2 key. A file
  // already here with the same size is left alone, so re-running a big folder is cheap and a partial
  // run can be finished later. Same name = overwrite, so this doubles as a refresh from the drive.
  const uploadMany = async (picked: File[], nestedSkipped = 0, renameMap?: Map<string, string>) => {
    const files = picked.filter((f) => !isJunk(f.name));
    if (files.length === 0) {
      setMsg(nestedSkipped ? `Nothing to upload (${nestedSkipped} file(s) were in sub-folders).` : 'No uploadable files were selected.');
      return;
    }
    setBusy(true);
    try {
      const present = new Map((data?.files ?? []).map((f) => [f.name, f.size]));

      // resolve name clashes within this batch so two local files never overwrite each other
      const used = new Set<string>();
      const uniqueName = (raw: string) => {
        let name = raw;
        while (used.has(name)) {
          const dot = raw.lastIndexOf('.');
          const base = dot > 0 ? raw.slice(0, dot) : raw;
          const ext = dot > 0 ? raw.slice(dot) : '';
          let i = 2;
          while (used.has(`${base} (${i})${ext}`)) i++;
          name = `${base} (${i})${ext}`;
        }
        used.add(name);
        return name;
      };

      let ok = 0;
      let unchanged = 0;
      let waits = 0;
      const failed: string[] = [];
      const total = files.length;
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (!f) continue;
        const clean = uniqueName(safeFileName(renameMap?.get(f.name) ?? f.name));
        if (present.get(clean) === f.size) { unchanged++; continue; } // already online, byte-for-byte
        const file = clean === f.name ? f : new File([f], clean, { type: f.type });
        try {
          await upload.mutateAsync({ prefix, file });
          ok++;
          setMsg(`Uploading… ${ok + unchanged}/${total}`);
        } catch (e) {
          if (e instanceof ApiError && e.status === 429 && waits < MAX_RATE_WAITS) {
            waits++;
            setMsg(`Rate limit reached - pausing, then continuing (${ok} uploaded, ${total - ok - unchanged} to go)…`);
            await sleep(RATE_WAIT_MS);
            i--; // retry this same file once the window resets
            continue;
          }
          failed.push(clean);
        }
      }
      const parts = [`Uploaded ${ok} file(s)`];
      if (unchanged) parts.push(`${unchanged} already current`);
      if (nestedSkipped) parts.push(`${nestedSkipped} in sub-folders ignored`);
      if (failed.length) parts.push(`${failed.length} failed: ${failed.slice(0, 5).join(', ')}${failed.length > 5 ? '…' : ''}`);
      setMsg(parts.join(' · ') + '.');
      void refetch();
    } finally {
      setBusy(false);
    }
  };

  // "refresh from folder" — the local directory picker (webkitdirectory) returns the whole tree, so
  // keep only the picked folder's own files: nested sub-folders would otherwise flatten together and
  // same-named files across them would collide. Plain multi-select files (no path) are always kept.
  const refreshFromFolder = (fs: FileList) => {
    const all = Array.from(fs);
    const own = all.filter((f) => {
      const rel = f.webkitRelativePath || '';
      return rel === '' || rel.split('/').length <= 2;
    });
    const skipped = all.length - own.length;
    // build name → SAP from the season's vessels, then see which files should gain a SAP prefix.
    const sapByName = new Map<string, string>();
    for (const v of overview.data?.vessels ?? []) {
      if (v.name && looksLikeSap(v.sap)) sapByName.set(normName(v.name), v.sap);
    }
    const onlineNames = (data?.files ?? []).map((f) => f.name);
    const proposals = proposeSapRenames(own.filter((f) => !isJunk(f.name)).map((f) => f.name), onlineNames, sapByName);
    if (proposals.length) { setRenamePlan({ files: own, skipped, proposals }); return; }
    void uploadMany(own, skipped);
  };

  // resolve the SAP-rename popup: either apply the SAP-prefixed names (and delete the superseded
  // copies) or upload the files exactly as picked.
  const applyRenames = async (apply: boolean) => {
    const plan = renamePlan;
    if (!plan) return;
    setRenamePlan(null);
    if (!apply) { void uploadMany(plan.files, plan.skipped); return; }
    const renameMap = new Map(plan.proposals.map((p) => [p.originalName, p.newName] as const));
    await uploadMany(plan.files, plan.skipped, renameMap);
    const byKey = new Map((data?.files ?? []).map((f) => [f.name, f.key] as const));
    const superseded = new Set<string>();
    for (const p of plan.proposals) for (const s of p.supersedes) superseded.add(s);
    for (const name of superseded) {
      const key = byKey.get(name);
      if (key) { try { await del.mutateAsync(key); } catch { /* best-effort cleanup */ } }
    }
    void refetch();
  };

  // create a season folder (named like the local one) so order lists can be filed under it.
  const createSeasonFolder = async (name: string) => {
    const clean = safeName(name);
    if (!clean) { setMsg('That name has no usable characters.'); return; }
    setMsg('');
    try {
      await mkdir.mutateAsync({ prefix, name: clean });
      setMsg(`Created ${clean}.`);
      void refetch();
    } catch {
      setMsg('Could not create the folder.');
    }
  };
  const createCustomFolder = () => setNewFolder(seasonLabel);
  const submitNewFolder = () => {
    if (newFolder == null || !newFolder.trim()) return;
    const n = newFolder;
    setNewFolder(null);
    void createSeasonFolder(n);
  };

  const files = data?.files ?? [];
  const folders = data?.folders ?? [];
  const title = ['Order Lists', ...segs].join(' / ');
  // the phone breadcrumb is a one-line scroller: keep the current folder (its end) in view
  useEffect(() => {
    const el = crumbs.current;
    if (el && el.scrollWidth > el.clientWidth) el.scrollLeft = el.scrollWidth;
  }, [prefix]);

  return (
    <>
      <PageHeader title={isPhone ? 'Info / Files' : `Info / Files - ${title}`}>
        {canWrite && level === 'seasons' && (
          <>
            <button className="btn btn-sm btn-primary" disabled={mkdir.isPending} onClick={() => void createSeasonFolder(seasonLabel)}>
              {mkdir.isPending ? 'Creating…' : `+ ${seasonLabel}`}
            </button>
            {!isPhone && <button className="btn btn-sm btn-ghost" disabled={mkdir.isPending} onClick={createCustomFolder}>+ Folder…</button>}
          </>
        )}
        {canWrite && (
          <button className="btn btn-sm btn-primary" onClick={() => setPicking(true)} title="Fill in a boat’s order from the template; it goes to the Order Sheet and into this folder">
            + Order file
          </button>
        )}
        {canWrite && (
          <>
            <input
              ref={fileInput}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const fs = e.target.files;
                if (fs && fs.length) void uploadMany(Array.from(fs));
                e.target.value = '';
              }}
            />
            <button className="btn btn-sm" disabled={busy || upload.isPending} onClick={() => fileInput.current?.click()}>
              {busy || upload.isPending ? 'Uploading…' : <><IconUpload size={14} /> Upload</>}
            </button>
          </>
        )}
        {canWrite && level === 'files' && (
          <input
            ref={(el) => {
              folderInput.current = el;
              if (el && !el.hasAttribute('webkitdirectory')) el.setAttribute('webkitdirectory', '');
            }}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const fs = e.target.files;
              if (fs && fs.length) refreshFromFolder(fs);
              e.target.value = '';
            }}
          />
        )}
        <OverflowActions
          title="Order Lists"
          items={[
            // phones: the custom folder joins the ⋯ so the season folder and Upload keep one row
            isPhone && canWrite && level === 'seasons' && { label: '+ Folder…', disabled: mkdir.isPending, onClick: createCustomFolder },
            canWrite && level === 'files' && {
              label: coarse ? 'Upload several…' : 'Refresh from folder',
              icon: <IconRefresh size={14} />,
              disabled: busy || upload.isPending,
              title: coarse
                ? 'Pick several files - each is uploaded here and same-named files are overwritten. Nothing already online is deleted.'
                : 'Pick a local folder - every file in it is uploaded here and same-named files are overwritten. Nothing already online is deleted.',
              onClick: () => folderInput.current?.click(),
            },
            { label: isFetching ? 'Refreshing…' : 'Refresh', disabled: isFetching, onClick: () => void refetch() },
          ]}
        />
      </PageHeader>

      <div className="toolbar if-crumbs" style={{ gap: 4 }} ref={crumbs}>
        <RootSwitch at="orders" onOrderLists={() => setPrefix(ORDER_LISTS_PREFIX)} onInfocenter={onInfocenter} />
        {segs.map((s, i) => (
          <span key={s}>
            <span className="muted"> / </span>
            <button className="btn btn-sm btn-ghost" onClick={() => setPrefix(ORDER_LISTS_PREFIX + segs.slice(0, i + 1).join('/') + '/')}>{s}</button>
          </span>
        ))}
      </div>

      {/* always mounted so screen readers announce each progress update */}
      <div className="muted" role="status" style={msg ? { marginBottom: 8 } : undefined}>{msg}</div>
      {/* upload outcomes are summarised in the status line above; ErrorState stays for the listing load */}
      {(del.error ?? mkdir.error) && <InlineError error={del.error ?? mkdir.error} onDismiss={() => { del.reset(); mkdir.reset(); }} />}
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {level === 'files' && (
        <div className="toolbar if-models" style={{ gap: 4, marginBottom: 8 }}>
          <span className="muted" style={{ fontSize: 12, marginRight: 4, alignSelf: 'center' }}>Model</span>
          <button
            className={`btn btn-sm ${!activeModel ? 'btn-primary' : 'btn-ghost'}`}
            aria-pressed={!activeModel}
            onClick={() => setPrefix(`${ORDER_LISTS_PREFIX}${segs[0]}/`)}
            title="All models in this season"
          >All</button>
          {MODEL_ORDER.map((m) => (
            <button
              key={m}
              className={`btn btn-sm ${activeModel === m ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={activeModel === m}
              onClick={() => setPrefix(`${ORDER_LISTS_PREFIX}${segs[0]}/${m}/`)}
              title={`Order files for ${m} - uploads land here`}
            >{m}</button>
          ))}
        </div>
      )}

      {data && (
        <div className="tbl-wrap">
          <table className="tbl tbl-cards if-tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Size</th>
                <th>Modified</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {folders.map((f) => (
                <tr key={f.prefix} className="if-folder">
                  <td>
                    <button className="btn btn-sm btn-ghost if-folder-btn" onClick={() => setPrefix(f.prefix)}>
                      <IconFolder size={15} /> {f.name.replace(/\/$/, '')}
                      <span className="if-chev" aria-hidden="true"><IconChevronRight size={16} /></span>
                    </button>
                  </td>
                  <td className="muted tc-hide">-</td>
                  <td className="muted tc-hide">-</td>
                  <td className="tc-hide"></td>
                </tr>
              ))}
              {files.map((f) => {
                const size = fmtSize(f.size);
                const modified = df.dateTime(f.uploaded);
                return (
                  <tr key={f.key}>
                    <td className="tc-title if-name">
                      <IconFile size={15} /> {f.name}
                      <span className="if-file-meta">{size} · {modified}</span>
                    </td>
                    <td className="muted tc-hide">{size}</td>
                    <td className="muted tc-hide">{modified}</td>
                    <td className="tc-actions if-acts" style={{ whiteSpace: 'nowrap' }}>
                      {isSpreadsheet(f.name) && (
                        <button className="btn btn-sm" onClick={() => setViewFile({ key: f.key, name: f.name })}>View</button>
                      )}
                      <a className="btn btn-sm" href={`/api/files/download?key=${encodeURIComponent(f.key)}`}>Download</a>
                      {canWrite && (
                        <button
                          className="btn btn-sm btn-ghost btn-del if-del"
                          aria-label={`Delete ${f.name}`}
                          title={`Delete ${f.name}`}
                          disabled={del.isPending}
                          onClick={async () => {
                            if (await confirm({ title: 'Delete file', message: `Delete “${f.name}”? This cannot be undone.`, confirmLabel: 'Delete', danger: true })) del.mutate(f.key);
                          }}
                        ><IconTrash size={14} /> Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {folders.length === 0 && files.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <EmptyState
                      title={level === 'seasons' ? 'No season folders yet' : 'This folder is empty'}
                      hint={
                        !canWrite ? undefined
                          : level === 'seasons' ? `Create the ${seasonLabel} folder to start.`
                          : 'Upload the order sheets, or refresh them from the local folder.'
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewFile && (
        <WorkbookViewerModal fileKey={viewFile.key} name={viewFile.name} onClose={() => setViewFile(null)} />
      )}

      {renamePlan && (
        <Modal
          title="Add SAP numbers to file names?"
          width={560}
          onClose={() => setRenamePlan(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => void applyRenames(false)}>Upload as-is</button>
              <button className="btn btn-primary" onClick={() => void applyRenames(true)}>Apply SAP names</button>
            </>
          }
        >
          <p className="muted" style={{ marginTop: 0 }}>
            {renamePlan.proposals.length} file(s) match a vessel with a known SAP.
            {renamePlan.proposals.some((p) => p.supersedes.length)
              ? ' Applying uploads them with the SAP in the name and removes the older copies that lack it.'
              : ' Applying uploads them with the SAP in the name.'}
          </p>
          <div className="if-rename-list">
            {renamePlan.proposals.map((p) => (
              <div key={p.originalName} style={{ padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13 }}><span className="muted">{p.originalName}</span> → <b>{p.newName}</b></div>
                {p.supersedes.length > 0 && (
                  <div className="muted" style={{ fontSize: 11 }}>replaces online: {p.supersedes.join(', ')}</div>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}

      {newFolder != null && (
        <Modal
          title="New Order Lists folder"
          onClose={() => setNewFolder(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setNewFolder(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={!newFolder.trim()} onClick={submitNewFolder}>Create</button>
            </>
          }
        >
          <label>
            <span className="field-label">Folder name</span>
            <input
              className="field"
              style={{ width: '100%' }}
              autoFocus
              value={newFolder}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setNewFolder(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitNewFolder(); } }}
            />
          </label>
        </Modal>
      )}
      {picking && <BoatPicker season={activeSeason} onClose={() => setPicking(false)} />}
      {confirmNode}
    </>
  );
}

/** "New order file": the season's boats, those with an order file already saying so; boats not on the
 *  Overview that already have one; and a new boat that isn't on the Overview (a stock boat, one not in
 *  Smartsheet yet), started by its model, name and, when it has one, its SAP */
function BoatPicker({ season, onClose }: { season: string; onClose: () => void }) {
  const overview = useOverview(season || undefined);
  const files = useOrderFiles(season);
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [nb, setNb] = useState({ model: '', name: '', sap: '' });
  const bySap = useMemo(() => new Map((files.data ?? []).map((f) => [f.sap, f])), [files.data]);
  const order = (m: string) => { const i = (MODEL_ORDER as readonly string[]).indexOf(modelKey(m)); return i < 0 ? 99 : i; };
  const boats = useMemo(() => (overview.data?.vessels ?? [])
    .filter((v) => v.sap && !v.left)
    .sort((a, b) => order(a.model) - order(b.model) || a.name.localeCompare(b.name)), [overview.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const listedSaps = useMemo(() => new Set((overview.data?.vessels ?? []).map((v) => v.sap)), [overview.data]);
  const unlisted = (files.data ?? []).filter((f) => !listedSaps.has(f.sap));
  const needle = fold(q.trim());
  const shown = needle ? boats.filter((v) => hasText(needle, v.name, v.sap, v.model)) : boats;
  const shownUnlisted = needle ? unlisted.filter((f) => hasText(needle, f.name, f.sap, f.model)) : unlisted;
  const link = (key: string, extra = '') => `/order-file/${encodeURIComponent(key)}?season=${encodeURIComponent(season)}${extra}`;
  const start = () => {
    const name = nb.name.trim().toUpperCase().replace(/\s+/g, ' ');
    const sap = nb.sap.trim();
    if (!name || !nb.model) return;
    onClose();
    navigate(link(sap || unlistedOrderKey(name), `&name=${encodeURIComponent(name)}&model=${encodeURIComponent(nb.model)}`));
  };
  const sapOnOverview = nb.sap.trim() && boats.find((v) => v.sap === nb.sap.trim());
  return (
    <Modal title={`New order file · season ${season}`} onClose={onClose} width={640}>
      <div className="if-pick-bar">
        <SearchInput value={q} onChange={setQ} placeholder="Boat, SAP or model" />
        <button className="btn btn-sm" aria-expanded={adding} onClick={() => setAdding(!adding)}>+ New boat…</button>
      </div>
      {adding && (
        <form className="if-newboat" onSubmit={(e) => { e.preventDefault(); start(); }}>
          <p className="muted">A boat that isn’t on the season’s Overview. Its name and model are kept on the Order Sheet with its order; the SAP can be added when it has one.</p>
          <label>
            <span className="field-label">Model</span>
            <select className="field" value={nb.model} onChange={(e) => setNb({ ...nb, model: e.target.value })} required>
              <option value="">Choose…</option>
              {MODEL_ORDER.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>
            <span className="field-label">Name</span>
            <input className="field" value={nb.name} onChange={(e) => setNb({ ...nb, name: e.target.value })} placeholder="e.g. STOCK GT7 1" required autoFocus />
          </label>
          <label>
            <span className="field-label">SAP (if it has one)</span>
            <input className="field" value={nb.sap} inputMode="numeric" onChange={(e) => setNb({ ...nb, sap: e.target.value.replace(/[^0-9A-Za-z]/g, '') })} />
          </label>
          {sapOnOverview
            ? <Link className="btn btn-primary" to={link(sapOnOverview.sap)} onClick={onClose}>That SAP is {sapOnOverview.name}: open it</Link>
            : <button className="btn btn-primary" type="submit" disabled={!nb.name.trim() || !nb.model}>Start the order file</button>}
        </form>
      )}
      {overview.isLoading && <LoadingState />}
      {overview.error && <ErrorState error={overview.error} />}
      {shownUnlisted.length > 0 && (
        <>
          <h3 className="if-pick-h">Not on the Overview</h3>
          <ul className="if-boats">
            {shownUnlisted.map((f) => (
              <li key={f.sap}>
                <Link to={link(f.sap)} onClick={onClose}>
                  <span className="if-boat-m">{f.model}</span>
                  <span className="if-boat-n">{f.name}</span>
                  <span className="muted if-boat-s">{orderKeySap(f.sap) || 'no SAP'}</span>
                  <span className="pill ok" title="Open it to change it">Order file · rev {f.revision}</span>
                </Link>
              </li>
            ))}
          </ul>
          <h3 className="if-pick-h">On the Overview</h3>
        </>
      )}
      <ul className="if-boats">
        {shown.map((v) => {
          const f = bySap.get(v.sap);
          return (
            <li key={v.sap}>
              <Link to={link(v.sap)} onClick={onClose}>
                <span className="if-boat-m">{modelKey(v.model)}</span>
                <span className="if-boat-n">{v.name}</span>
                <span className="muted if-boat-s">{v.sap}</span>
                {f ? <span className="pill ok" title="Open it to change it">Order file · rev {f.revision}</span> : <span className="muted if-boat-new">New</span>}
              </Link>
            </li>
          );
        })}
        {overview.data && !shown.length && <li className="muted">No boats match{adding ? '' : '. A boat that isn’t on the Overview can be started with + New boat'}.</li>}
      </ul>
    </Modal>
  );
}
